<script setup>
import {computed, inject, onMounted, ref} from "vue";
import Dialog from "primevue/dialog";
import {Icon} from "@iconify/vue";
import {Button, FloatLabel, InputGroup, InputGroupAddon, InputNumber} from "primevue";
import {useCurrencyPricesStore} from "@/stores/currencyPrices.js";
import {showMessage} from "@/utils/showMessage.js";
import Loading from "@/components/Icon/Loading.vue";

const props = defineProps({
  store: {
    type: String,
    default: '',
  },
})
const emit = defineEmits(['apply'])

const visible = inject('isShowPriceCalculatorModal')
const currencyPricesStore = useCurrencyPricesStore()
const currencyPrices = ref([])
const isLoadingCurrencies = ref(false)
const amount = ref(null)
const rate = ref(null)
const value = ref(0)
const hasCalculatedValue = ref(false)
const storeCurrencyMap = {
  'WoWMarket (Dawn)': 'Gold Dawn',
  'WoWMarket (Gold)': 'Gold Retail',
  'WoWMarket (Oblivion)': 'Gold Oblivion',
  'WoWMarket (Sylvanas)': 'Gold Sylvanas',
}

const currencyName = computed(() => storeCurrencyMap[props.store] || '')
const selectedCurrency = computed(() => (
    currencyPrices.value.find(currency => currency.name === currencyName.value) || null
))

const formatNumber = (number, decimals = 2) => (
    number.toFixed(decimals).replace(/\.?0+$/, '')
)

const calculateValue = () => {
  const amountNumber = Number(amount.value)
  const rateNumber = Number(rate.value)

  if (
      amount.value === null ||
      amount.value === '' ||
      rate.value === null ||
      rate.value === '' ||
      !Number.isFinite(rateNumber)
  ) {
    value.value = 0
    hasCalculatedValue.value = false
    return
  }

  value.value = formatNumber(amountNumber * rateNumber)
  hasCalculatedValue.value = true
}

const onAmountInput = (event) => {
  amount.value = event.value
  calculateValue()
}

const onRateInput = (event) => {
  const nextRate = event.target.value

  if (!/^\d*(?:\.\d*)?$/.test(nextRate)) {
    event.target.value = rate.value ?? ''
    return
  }

  rate.value = nextRate
  calculateValue()
}

const applyValue = () => {
  if (!hasCalculatedValue.value) return

  emit('apply', value.value)
  visible.value = false
}

const getCurrencies = async () => {
  try {
    isLoadingCurrencies.value = true
    const res = await currencyPricesStore.action_getPrices()
    if (res.result === true) {
      currencyPrices.value = res.data
      rate.value = selectedCurrency.value?.price
          ? Number(selectedCurrency.value.price)
          : null
      calculateValue()
    } else {
      showMessage(res, 'error')
    }
  } catch (err) {
    showMessage(err, 'error')
  } finally {
    isLoadingCurrencies.value = false
  }
}

onMounted(() => {
  getCurrencies()
})
</script>

<template>
  <Dialog
      v-model:visible="visible"
      class="w-full max-w-xl !border-0 !rounded-md !bg-[#0e1726]"
      closable
      dismissable-mask
      draggable
      modal
      :pt="{header: {class: '!h-max'}}"
  >
    <template #header>
      <div class="text-base py-3 !h-max">
        {{ store }} Price Calculator
      </div>
    </template>

    <template #closebutton="{closeCallback}">
      <div class="h-full w-[1px] bg-slate-700"></div>
      <button
          type="button"
          class="text-white-dark outline-none hover:text-red-500"
          @click="closeCallback"
      >
        <Icon icon="pajamas:close-xs"/>
      </button>
    </template>

    <div class="space-y-4 py-4 text-sm text-slate-600 dark:text-slate-300">
      <div class="rounded-md border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-900/50">
        <div class="flex items-center justify-between gap-4">
          <span class="text-slate-500 dark:text-slate-400">Calculation currency</span>
          <span class="font-medium text-slate-900 dark:text-white">{{ currencyName }}</span>
        </div>
        <div class="mt-3 flex items-center justify-between gap-4 border-t border-slate-200 pt-3 dark:border-slate-700">
          <span class="text-slate-500 dark:text-slate-400">Current rate</span>
          <div class="flex items-center gap-3">
            <input
                :value="rate"
                type="text"
                inputmode="decimal"
                aria-label="Current rate"
                :disabled="isLoadingCurrencies"
                class="price-calculator-rate w-16 text-center appearance-none border-0 border-b border-dashed border-slate-500 bg-transparent px-0 py-0.5 font-medium text-slate-900 outline-none transition-colors focus:border-primary-500 focus:ring-0 disabled:cursor-not-allowed disabled:opacity-50 dark:text-white"
                @input="onRateInput"
            />
            <button
                type="button"
                class="text-slate-500 transition-colors hover:text-primary-500 disabled:cursor-not-allowed disabled:opacity-50 dark:text-slate-400"
                :disabled="isLoadingCurrencies"
                title="Refresh currency prices"
                @click="getCurrencies"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                   :class="{'animate-spin': isLoadingCurrencies}">
                <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/>
                <path d="M21 3v5h-5"/>
                <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/>
                <path d="M8 16H3v5"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
      <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div class="flex flex-col gap-0.5 text-xs 2xl:text-sm">
          <InputGroup class="w-full">
            <FloatLabel variant="on">
              <InputNumber
                  id="priceCalculatorAmount"
                  v-model="amount"
                  autocomplete="off"
                  :max-fraction-digits="2"
                  input-class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"
                  @input="onAmountInput"
              />
              <label for="priceCalculatorAmount" class="!bg-slate-900">Amount</label>
            </FloatLabel>
            <InputGroupAddon
                class="!bg-transparent !border-slate-700 h-[32px] 2xl:h-[40px] text-[.7rem] whitespace-nowrap w-auto flex-shrink-0 !px-1"
            >
              {{ currencyName }}
            </InputGroupAddon>
          </InputGroup>
        </div>

        <div class="flex flex-col gap-0.5 text-xs 2xl:text-sm">
          <InputGroup class="w-full">
            <FloatLabel variant="on">
              <InputNumber
                  id="priceCalculatorValue"
                  v-model="value"
                  autocomplete="off"
                  readonly
                  disabled
                  :max-fraction-digits="2"
                  :input-class="[
                    '!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700',
                    'p-filled'
                  ]"
              />
              <label for="priceCalculatorValue" class="!bg-slate-900">Value</label>
            </FloatLabel>
            <InputGroupAddon class="!bg-transparent !border-slate-700 h-[32px] 2xl:h-[40px] text-xs">
              $
            </InputGroupAddon>
          </InputGroup>
        </div>
      </div>
    </div>

    <template #footer>
      <button type="button" class="btn btn-outline-dark py-1" @click="visible = false">
        Close
      </button>
      <button
          type="button"
          class="btn btn-outline-primary py-1 disabled:opacity-50 disabled:cursor-not-allowed"
          :disabled="!hasCalculatedValue"
          @click="applyValue"
      >
        Save
      </button>

    </template>
  </Dialog>
</template>

<style scoped>
.price-calculator-rate {
  -moz-appearance: textfield;
}

.price-calculator-rate::-webkit-inner-spin-button,
.price-calculator-rate::-webkit-outer-spin-button {
  margin: 0;
  -webkit-appearance: none;
}
</style>
